import {
  build_table_cell,
  build_table_column,
  build_placeholder_label_from_binding,
  find_question_option,
  sync_binding_source,
} from "./formal_document_template_helpers";
import { ensure_formal_document_binding } from "./formal_document_template_binding_helpers";
import { get_bound_question_ids } from "./formal_document_template_selection_helpers";

const build_static_row = (column_count) =>
  [...Array(column_count || 1)].map(() => build_table_cell());

export default {
  name: "FormalDocumentTemplateDataTableEditor",
  props: {
    question_options: {
      type: Array,
      default: () => [],
    },
    section: {
      type: Object,
      required: true,
    },
    system_field_options: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["open-question-picker"],
  computed: {
    uses_static_rows() {
      return this.get_row_source_type() === "static_rows";
    },
    columns() {
      return this.getColumns();
    },
    rows() {
      return this.getRows();
    },
    row_source_type() {
      return this.get_row_source_type();
    },
    row_source_question_ids() {
      return this.get_row_source_question_ids();
    },
  },
  created() {
    this.ensure_data_table_config();
  },
  methods: {
    ensure_binding(binding, default_source_type = "system_field") {
      const normalized_binding = ensure_formal_document_binding(
        binding,
        default_source_type
      );

      return normalized_binding;
    },
    ensure_data_table_config() {
      if (!this.section.config.row_source) {
        this.section.config.row_source = {
          source_type: "static_rows",
          question_ids: [],
        };
      }
      if (!Array.isArray(this.section.config.row_source.question_ids)) {
        this.section.config.row_source.question_ids = [];
      }
      const raw_columns = Array.isArray(this.section.config.columns)
        ? this.section.config.columns
        : [];
      this.section.config.columns = raw_columns.length
        ? raw_columns.map((column) => this.normalize_column(column))
        : [build_table_column()];
      const raw_rows = Array.isArray(this.section.config.rows)
        ? this.section.config.rows
        : [];
      this.section.config.rows = this.normalize_rows(
        raw_rows,
        this.section.config.columns.length
      );
    },
    normalize_column(column) {
      if (!column || typeof column !== "object" || Array.isArray(column)) {
        return build_table_column();
      }
      if (!column.local_id) {
        column.local_id = build_table_column().local_id;
      }
      if (column.label === undefined) {
        column.label = "";
      }
      column.binding = this.ensure_binding(column.binding);
      return column;
    },
    normalize_rows(rows, column_count) {
      if (!rows.length) {
        return [build_static_row(column_count)];
      }
      return rows.map((row) => this.normalize_row(row, column_count));
    },
    normalize_row(row, column_count) {
      if (!Array.isArray(row)) {
        return build_static_row(column_count);
      }
      const normalized_row = [...Array(column_count)].map((_, index) => {
        const cell = Reflect.get(row, index);
        if (!cell || typeof cell !== "object" || Array.isArray(cell)) {
          return build_table_cell();
        }
        if (!cell.local_id) {
          cell.local_id = build_table_cell().local_id;
        }
        cell.binding = this.ensure_binding(cell.binding, "static_text");
        return cell;
      });
      return normalized_row;
    },
    get_row_source_type() {
      this.ensure_data_table_config();
      return this.section.config?.row_source?.source_type || "static_rows";
    },
    get_row_source_question_ids() {
      this.ensure_data_table_config();
      return this.section.config?.row_source?.question_ids || [];
    },
    get_column_binding_source_type(column) {
      column.binding = this.ensure_binding(column?.binding);
      return column?.binding?.source_type || "system_field";
    },
    getColumns() {
      this.ensure_data_table_config();
      return this.section.config?.columns || [];
    },
    getRows() {
      this.ensure_data_table_config();
      return this.section.config?.rows || [];
    },
    get_label_placeholder(target_row) {
      return build_placeholder_label_from_binding(
        target_row?.binding,
        this.question_options
      );
    },
    set_row_source_type(next_value) {
      this.ensure_data_table_config();
      this.section.config.row_source.source_type = next_value;
    },
    add_table_column() {
      this.ensure_data_table_config();
      this.section.config.columns.push(build_table_column());
      this.add_cells_to_static_rows();
    },
    remove_table_column(local_id) {
      const column_index = this.section.config.columns.findIndex(
        (column) => column.local_id === local_id
      );
      this.section.config.columns = this.section.config.columns.filter(
        (column) => column.local_id !== local_id
      );
      this.remove_cells_from_static_rows(column_index);
    },
    add_table_row() {
      this.ensure_data_table_config();
      this.section.config.rows.push(
        build_static_row(this.section.config.columns.length)
      );
    },
    remove_table_row(row_index) {
      this.section.config.rows.splice(row_index, 1);
      if (!this.section.config.rows.length) {
        this.add_table_row();
      }
    },
    handle_binding_source_change(binding, label_target) {
      sync_binding_source(binding);
      return label_target;
    },
    handle_column_source_selection(column, next_source_type) {
      if (next_source_type === "section_question") {
        this.open_bulk_column_picker();
        return;
      }
      column.binding.source_type = next_source_type;
      this.handle_binding_source_change(column.binding, column);
    },
    handle_label_target_change(label_target) {
      return label_target;
    },
    get_question_label(section_question_id) {
      return (
        find_question_option(this.question_options, section_question_id)?.label ||
        "No question selected"
      );
    },
    open_column_question_picker(column) {
      this.$emit("open-question-picker", {
        section_local_id: this.section.local_id,
        selection_mode: "single",
        selected_question_ids: column.binding.section_question_id
          ? [column.binding.section_question_id]
          : [],
        target_local_id: column.local_id,
        target_type: "table_column",
      });
    },
    open_repeatable_question_picker() {
      this.$emit("open-question-picker", {
        section_local_id: this.section.local_id,
        selection_mode: "multiple",
        selected_question_ids: [...this.get_row_source_question_ids()],
        target_local_id: "row_source_questions",
        target_type: "row_source_questions",
      });
    },
    open_bulk_column_picker() {
      this.$emit("open-question-picker", {
        section_local_id: this.section.local_id,
        selection_mode: "multiple",
        selected_question_ids: get_bound_question_ids(this.getColumns()),
        target_local_id: "table_columns_bulk",
        target_type: "table_columns_bulk",
      });
    },
    get_column_question_count() {
      return get_bound_question_ids(this.getColumns()).length;
    },
    get_repeatable_question_count() {
      return this.get_row_source_question_ids().length;
    },
    add_cells_to_static_rows() {
      if (!this.uses_static_rows) {
        return;
      }
      this.section.config.rows = (this.section.config.rows || []).map((row) => [
        ...row,
        build_table_cell(),
      ]);
    },
    remove_cells_from_static_rows(column_index) {
      if (!this.uses_static_rows || column_index < 0) {
        return;
      }
      this.section.config.rows = (this.section.config.rows || []).map((row) =>
        row.filter((cell, index) => index !== column_index)
      );
    },
  },
};
